import {action, computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import errorToast from "../../components/toast/ErrorToast";
import successToast from "../../components/toast/SuccessToast";
import TwoDecimalsMoneyRenderer from "../../utils/TwoDecimalsMoneyRenderer";
import PaymentReminderFeature from "./payment_reminder/PaymentReminderFeature";

export default class CurrentAccountFeature {
    constructor(application, navigator, id) {
        this._application = application;
        this._navigator = navigator;
        this._loaded = false;
        this._businessName = id;
        this._currentAccount = null;
        this._accountingDocuments = [];
        this._hasPendingJudicialManagementRequest = false;
        this._judicialStageModalOpen = false;
        this._judicialStageReason = '';
        this._sendingJudicialStageAdvanceRequest = false;
        this._primaryRecord = null;
        this._exportingPrimaryRecord = false;
        this._selectedView = 'applied';
        this._paymentReminder = null;
        this._hasCollectionsEmail = false;
        this._paymentReminderFeature = new PaymentReminderFeature(application);

        makeObservable(this, {
            _loaded: observable,
            loaded: computed,
            _currentAccount: observable,
            currentAccount: computed,
            _accountingDocuments: observable,
            accountingDocuments: computed,
            _hasPendingJudicialManagementRequest: observable,
            _judicialStageModalOpen: observable,
            judicialStageModalOpen: computed,
            _judicialStageReason: observable,
            judicialStageReason: computed,
            setJudicialStageReason: action,
            openJudicialStageModal: action,
            closeJudicialStageModal: action,
            _sendingJudicialStageAdvanceRequest: observable,
            requestJudicialStageAdvance: action,
            _markJudicialStageAdvanceRequestAsSent: action,
            _markJudicialStageAdvanceRequestAsFailed: action,
            _exportingPrimaryRecord: observable,
            isExportingPrimaryRecord: computed,
            exportPrimaryRecord: action,
            _markPrimaryRecordExportAsFinished: action,
            _primaryRecord: observable,
            primaryRecord: computed,
            _selectedView: observable,
            selectedView: computed,
            selectView: action,
            _paymentReminder: observable,
            paymentReminder: computed,
            _hasCollectionsEmail: observable,
            hasCollectionsEmail: computed,
            _finishLoading: action
        })
    }

    async load() {
        await this._currentAccountAndAccountingDocuments()
        await this._customerContacts()
        this._finishLoading()
    }

    customersBusinessName() {
        return this._currentAccount.customer.businessName
    }

    customersFantasyName() {
        return this._currentAccount.customer.fantasyName
    }

    channel() {
        return this._currentAccount.channel
    }

    paymentTerm() {
        return this._currentAccount.paymentTerm
    }

    worstTier() {
        return this._currentAccount.worstTier
    }

    hasActiveOrder() {
        return this._currentAccount.hasOrderWithFutureDelivery
    }

    outstandingBalance() {
        return TwoDecimalsMoneyRenderer(this._currentAccount.outstandingBalance.amount)
    }

    overdueBalance() {
        return TwoDecimalsMoneyRenderer(this._currentAccount.overdueBalance.amount)
    }

    unassignedBalance() {
        return TwoDecimalsMoneyRenderer(this._currentAccount.unassignedBalance.amount)
    }

    totalBalance() {
        return TwoDecimalsMoneyRenderer(this._currentAccount.totalBalance.amount)
    }

    isHighRisk() {
        return this._currentAccount.isHighRisk
    }

    selectView(view) {
        this._selectedView = view;
    }

    isPrimaryRecordViewSelected() {
        return this.selectedView === 'primaryRecord';
    }

    primaryRecordRows() {
        const documentRows = this.primaryRecord.documentsWithRunningBalance.map(
            documentWithRunningBalance => this._presentedDocumentRow(documentWithRunningBalance));
        return [...documentRows, this._presentedInitialBalanceRow()];
    }

    _presentedDocumentRow(documentWithRunningBalance) {
        const isDebit = documentWithRunningBalance.isDebit;
        const presentedTotal = TwoDecimalsMoneyRenderer(documentWithRunningBalance.total.amount);
        return {
            date: documentWithRunningBalance.emissionDate,
            type: documentWithRunningBalance.typeName,
            documentLabel: documentWithRunningBalance.documentLabel,
            hasFile: documentWithRunningBalance.hasFile,
            debit: isDebit ? presentedTotal : null,
            hasDebit: isDebit,
            credit: isDebit ? null : presentedTotal,
            hasCredit: !isDebit,
            balance: TwoDecimalsMoneyRenderer(documentWithRunningBalance.balance.amount),
            isInitialBalance: false,
        }
    }

    _presentedInitialBalanceRow() {
        return {
            date: '-',
            type: 'Saldo Inicial',
            documentLabel: '-',
            hasFile: false,
            debit: null,
            hasDebit: false,
            credit: null,
            hasCredit: false,
            balance: TwoDecimalsMoneyRenderer(this.primaryRecord.initialBalance.amount),
            isInitialBalance: true,
        }
    }

    editRiskClassification(highRisk) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this.currentAccount = {...this._currentAccount, isHighRisk: response.isHighRisk()};
                successToast("Clasificación de riesgo actualizada");
            },
            handlesError: () => {
                errorToast("No se pudo actualizar la clasificación de riesgo");
            }
        });
        return this._application.apiClient().editCurrentAccountRiskClassification(
            responseHandler, this._businessName, highRisk
        );
    }

    currentJudicialStageIdentifier() {
        return this._currentAccount.judicialStage;
    }

    showsWorstTierBadge() {
        const worstTier = this.worstTier();
        return Boolean(worstTier) && worstTier.code !== this.currentJudicialStageIdentifier();
    }

    currentJudicialStageLabel() {
        return this._judicialStageLabelOf(this.currentJudicialStageIdentifier());
    }

    nextJudicialStageLabel() {
        const nextJudicialStageIdentifierByCurrentStage = {
            [this._withoutJudicialManagementStageIdentifier()]: this._prejudicialManagementStageIdentifier(),
            [this._prejudicialManagementStageIdentifier()]: this._judicialManagementStageIdentifier(),
        };
        return this._judicialStageLabelOf(nextJudicialStageIdentifierByCurrentStage[this.currentJudicialStageIdentifier()]);
    }

    _judicialStageLabelOf(judicialStageIdentifier) {
        const judicialStageLabelsByIdentifier = {
            [this._prejudicialManagementStageIdentifier()]: 'Pre Gestión Judicial',
            [this._judicialManagementStageIdentifier()]: 'Gestión Judicial',
        };
        return judicialStageLabelsByIdentifier[judicialStageIdentifier] ?? null;
    }

    _withoutJudicialManagementStageIdentifier() {
        return 'without_judicial_management';
    }

    _prejudicialManagementStageIdentifier() {
        return 'prejudicial_management';
    }

    _judicialManagementStageIdentifier() {
        return 'judicial_management';
    }

    hasPendingJudicialStageRequest() {
        return this._hasPendingJudicialManagementRequest;
    }

    pendingJudicialStageRequestLabel() {
        return `Solicitud de ${this.nextJudicialStageLabel()} pendiente`;
    }

    sendingJudicialStageAdvanceRequestLabel() {
        return `Enviando solicitud de ${this.nextJudicialStageLabel()}...`;
    }

    judicialStageButtonLabel() {
        if (this._sendingJudicialStageAdvanceRequest) {
            return this.sendingJudicialStageAdvanceRequestLabel();
        }
        return this.hasPendingJudicialStageRequest() ? this.pendingJudicialStageRequestLabel() : this.nextJudicialStageLabel();
    }

    judicialStageButtonIsDisabled() {
        return this._sendingJudicialStageAdvanceRequest || this.hasPendingJudicialStageRequest();
    }

    showsJudicialStageButton() {
        const currentJudicialStage = this._currentAccount.judicialStage;
        if (currentJudicialStage === this._judicialManagementStageIdentifier()) {
            return false;
        }
        if (currentJudicialStage === this._prejudicialManagementStageIdentifier()) {
            return true;
        }
        return this._hasInvoiceOverdueBeyondJudicialManagementThreshold();
    }

    openJudicialStageModal() {
        this._judicialStageModalOpen = true;
    }

    closeJudicialStageModal() {
        this._judicialStageModalOpen = false;
        this._judicialStageReason = '';
    }

    setJudicialStageReason(reason) {
        this._judicialStageReason = reason;
    }

    requestJudicialStageAdvance() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: () => {
                this._markJudicialStageAdvanceRequestAsSent();
                successToast('Solicitud de avance de etapa judicial enviada');
            },
            handlesError: () => {
                this._markJudicialStageAdvanceRequestAsFailed();
                errorToast('No se pudo solicitar el avance de etapa judicial');
            }
        });
        this._sendingJudicialStageAdvanceRequest = true;
        this._judicialStageModalOpen = false;
        return this._application.apiClient().requestJudicialStageAdvance(
            this._businessName, this._judicialStageReason, responseHandler);
    }

    _markJudicialStageAdvanceRequestAsSent() {
        this._sendingJudicialStageAdvanceRequest = false;
        this._hasPendingJudicialManagementRequest = true;
        this._judicialStageReason = '';
    }

    _markJudicialStageAdvanceRequestAsFailed() {
        this._sendingJudicialStageAdvanceRequest = false;
    }

    get isExportingPrimaryRecord() {
        return this._exportingPrimaryRecord;
    }

    exportPrimaryRecord() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: () => this._markPrimaryRecordExportAsFinished(),
            handlesError: () => {
                this._markPrimaryRecordExportAsFinished();
                errorToast('No se pudo exportar el documento por documento');
            }
        });
        this._exportingPrimaryRecord = true;
        return this._application.apiClient().exportCurrentAccountPrimaryRecord(this._businessName, responseHandler);
    }

    _markPrimaryRecordExportAsFinished() {
        this._exportingPrimaryRecord = false;
    }

    _hasInvoiceOverdueBeyondJudicialManagementThreshold() {
        const judicialStageAdvanceEligibilityThresholdInDays = 30;
        return this._debitAccountingDocuments().some(accountingDocument =>
            accountingDocument.pendingAmount.amount > 0 &&
            accountingDocument.overdueDays > judicialStageAdvanceEligibilityThresholdInDays);
    }

    invoiceColumns() {
        return [
            {id: 'numberWithPointOfSale', label: 'Factura', width: '1.4fr'},
            {id: 'emissionDate', label: 'Emisión', width: '1fr'},
            {id: 'expirationDate', label: 'Vencimiento', width: '1fr'},
            {id: 'daysLabel', label: 'Días', width: '0.9fr'},
            {id: 'statusLabel', label: 'Estado', width: '1.1fr'},
            {id: 'total', label: 'Importe', align: 'right', width: '1.1fr'},
            {id: 'appliedAmount', label: 'Aplicado', align: 'right', width: '1.1fr'},
            {id: 'pendingAmount', label: 'Saldo', align: 'right', width: '1.1fr'},
        ];
    }

    invoiceRows() {
        return this._debitAccountingDocuments().map(accountingDocument => this._presentedInvoiceRow(accountingDocument));
    }

    creditNoteRows() {
        return this._creditNoteAccountingDocuments().map(accountingDocument => this._presentedCreditRow(accountingDocument));
    }

    receiptRows() {
        return this._receiptAccountingDocuments().map(accountingDocument => this._presentedCreditRow(accountingDocument));
    }

    _debitAccountingDocuments() {
        return this.accountingDocuments.filter(accountingDocument => accountingDocument.group === 'invoices');
    }

    _creditNoteAccountingDocuments() {
        return this.accountingDocuments.filter(accountingDocument => accountingDocument.group === 'credit_notes');
    }

    _receiptAccountingDocuments() {
        return this.accountingDocuments.filter(accountingDocument => accountingDocument.group === 'receipts');
    }

    _presentedInvoiceRow(accountingDocument) {
        const totalAmount = accountingDocument.total.amount;
        const appliedAmount = accountingDocument.appliedAmount.amount;
        return {
            id: accountingDocument.id,
            numberWithPointOfSale: `${accountingDocument.type} ${accountingDocument.numberWithPointOfSale}`,
            type: accountingDocument.type,
            emissionDate: accountingDocument.emissionDate,
            expirationDate: accountingDocument.expirationDate,
            daysLabel: this._daysLabelFor(accountingDocument),
            overdueDays: accountingDocument.overdueDays,
            statusLabel: accountingDocument.status.label,
            statusCode: accountingDocument.status.code,
            total: TwoDecimalsMoneyRenderer(totalAmount),
            appliedAmount: TwoDecimalsMoneyRenderer(appliedAmount),
            pendingAmount: TwoDecimalsMoneyRenderer(accountingDocument.pendingAmount.amount),
            progressPercentage: this._progressPercentageOf(totalAmount, appliedAmount),
            applications: this._presentedApplications(
                accountingDocument.applications, application => application.appliedDocument),
            hasApplications: accountingDocument.applications.length > 0,
        }
    }

    _presentedCreditRow(accountingDocument) {
        return {
            id: accountingDocument.id,
            numberWithPointOfSale: `${accountingDocument.type} ${accountingDocument.numberWithPointOfSale}`,
            type: accountingDocument.type,
            emissionDate: accountingDocument.emissionDate,
            currency: accountingDocument.total.currency,
            total: TwoDecimalsMoneyRenderer(accountingDocument.total.amount),
            unappliedAmount: TwoDecimalsMoneyRenderer(accountingDocument.unappliedAmount.amount),
            hasUnappliedAmount: accountingDocument.unappliedAmount.amount > 0,
            applications: this._presentedApplications(
                accountingDocument.applications, application => application.debitDocument),
            hasApplications: accountingDocument.applications.length > 0,
        }
    }

    _daysLabelFor(accountingDocument) {
        const CANCELLED_STATUS_CODE = 'cancelled';
        if (accountingDocument.status.code === CANCELLED_STATUS_CODE) {
            return '—';
        }
        if (accountingDocument.overdueDays > 0) {
            return `${accountingDocument.overdueDays}d atraso`;
        }
        if (accountingDocument.overdueDays === 0) {
            return 'Vence hoy';
        }
        return `Vence en ${Math.abs(accountingDocument.overdueDays)}d`;
    }

    _progressPercentageOf(totalAmount, appliedAmount) {
        if (totalAmount <= 0) {
            return 0;
        }
        return Math.min(100, (appliedAmount / totalAmount) * 100);
    }

    _presentedApplications(applications, counterpartDocumentOf) {
        return applications.map(application => {
            const counterpartDocument = counterpartDocumentOf(application);
            return {
                type: counterpartDocument.type,
                numberWithPointOfSale: counterpartDocument.numberWithPointOfSale,
                issueDate: counterpartDocument.issueDate,
                appliedAmount: TwoDecimalsMoneyRenderer(application.appliedAmount.amount),
            }
        })
    }

    async _currentAccountAndAccountingDocuments() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this.currentAccount = response.currentAccount()
                this.accountingDocuments = response.accountingDocuments();
                this._hasPendingJudicialManagementRequest = response.hasPendingJudicialManagementRequest();
                this.primaryRecord = response.primaryRecord();
                this._paymentReminder = response.paymentReminder();
            },
            handlesError: (response) => {
                errorToast('No se pudo obtener la cuenta corriente')
            }
        });
        return this._application.apiClient().currentAccountAndAccountingDocuments(this._businessName, responseHandler)
    }

    async _customerContacts() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._hasCollectionsEmail = response.hasCollectionsEmail();
            },
            handlesError: () => {
                errorToast('No se pudieron obtener los contactos del cliente')
            }
        });
        return this._application.apiClient().customerContacts(responseHandler, this._businessName)
    }

    get paymentReminder() {
        return this._paymentReminder;
    }

    get hasCollectionsEmail() {
        return this._hasCollectionsEmail;
    }

    get paymentReminderFeature() {
        return this._paymentReminderFeature;
    }

    openPaymentReminder() {
        return this._paymentReminderFeature.open(this._currentAccount.customer.taxpayerIdentificationNumber);
    }

    hasHighRiskClassificationPermission() {
        return this._application.hasHighRiskClassificationPermission();
    }

    hasPaymentReminderPermission() {
        return this._application.hasPaymentReminderPermission();
    }

    get loaded() {
        return this._loaded;
    }

    get currentAccount() {
        return this._currentAccount;
    }

    set currentAccount(value) {
        this._currentAccount = value;
    }

    _finishLoading() {
        this._loaded = true;
    }

    get accountingDocuments() {
        return this._accountingDocuments;
    }

    set accountingDocuments(documents) {
        this._accountingDocuments = documents;
    }

    get judicialStageModalOpen() {
        return this._judicialStageModalOpen;
    }

    get judicialStageReason() {
        return this._judicialStageReason;
    }

    get primaryRecord() {
        return this._primaryRecord;
    }

    set primaryRecord(primaryRecord) {
        this._primaryRecord = primaryRecord;
    }

    get selectedView() {
        return this._selectedView;
    }
}
